import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAppStore } from '../../../shared/hooks/useAppStore';
import { useLoyaltyStore } from './useLoyaltyStore';

const GET_LOYALTY_BALANCE_QUERY_KEY = 'GET_LOYALTY_BALANCE_QUERY_KEY';

interface UseGetLoyaltyBalanceHookProps {
    /**
     * Decides if we should fetch data on mount or not, set to "true" by default
     */
    isFetchOnMountEnabled?: boolean;
    onFetchFinished?: () => void;
    /**
     * Asks again on this interval; `false` stops. A function so the caller can
     * decide from the store after each fetch — `queryFn` returns `null`, the
     * balance itself lives in `useLoyaltyStore`. Off unless passed.
     */
    refetchInterval?: () => number | false;
    /**
     * This observer's fetches re-read only the balance (`refreshBalance`) and
     * never fetch on mount. The query key stays shared, so a tick that fires
     * while the full fetch is in flight joins it instead of racing it — two
     * writers on one key would let a balance read before the purchase point
     * land after the newer one.
     */
    refreshBalanceOnly?: boolean;
}

export function useGetLoyaltyBalance({
    isFetchOnMountEnabled = true,
    onFetchFinished,
    refetchInterval,
    refreshBalanceOnly = false,
}: UseGetLoyaltyBalanceHookProps) {
    const fetchBalance = useLoyaltyStore((state) => state.fetchBalance);
    const refreshBalance = useLoyaltyStore((state) => state.refreshBalance);
    const loadBalance = refreshBalanceOnly ? refreshBalance : fetchBalance;
    const order = useAppStore((s) => s.order);
    const isValidDto =
        Boolean(Number(order.user_id)) && Boolean(Number(order.vm_id));

    const { isFetching, isFetched, refetch } = useQuery({
        queryKey: [
            GET_LOYALTY_BALANCE_QUERY_KEY,
            order.vm_id,
            order.user_id,
            isValidDto,
        ],
        queryFn: async () => {
            await loadBalance({
                userId: Number(order.user_id),
                vmId: Number(order.vm_id),
            });
            return null;
        },
        enabled: isFetchOnMountEnabled && isValidDto,
        refetchInterval: refetchInterval ?? false,
        refetchOnMount: !refreshBalanceOnly,
        retry: false,
    });

    useEffect(() => {
        if (isFetched) {
            onFetchFinished?.();
        }
    }, [isFetched]);

    return {
        isLoyaltyBalanceLoading: isFetching,
        fetchLoyaltyBalance: refetch,
    };
}
